import React, { useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import { makeStyles } from '@material-ui/core/styles'
import { startOfUsWeek } from '../schedule-date-ranges'

const HOUR_PX = 48
const TIME_COL_WIDTH = 52

/** 30-minute sessions are ~24px; tier labels so the title still fits. */
const EVENT_BLOCK_FULL_LAYOUT_MIN_PX = 40
const EVENT_BLOCK_TWO_LINE_MIN_PX = 28

/** 6 am → 9 pm default window; expands to fit events */
const DEFAULT_START_MIN = 6 * 60
const DEFAULT_END_MIN = 21 * 60

/** 20-slot coach color palette, distinct & CVD-friendly (non-red tones). */
const COACH_COLORS = [
  { bg: '#00897B', text: '#fff' }, // teal
  { bg: '#0075CA', text: '#fff' }, // PGA blue
  { bg: '#F4A100', text: '#0d1f0d' }, // gold
  { bg: '#5E35B1', text: '#fff' }, // deep purple
  { bg: '#00838F', text: '#fff' }, // cyan
  { bg: '#2E7D32', text: '#fff' }, // dark green
  { bg: '#EF6C00', text: '#fff' }, // deep orange
  { bg: '#AD1457', text: '#fff' }, // pink (not red)
  { bg: '#283593', text: '#fff' }, // indigo
  { bg: '#4E342E', text: '#fff' }, // brown
  { bg: '#558B2F', text: '#fff' }, // light-ish green
  { bg: '#00695C', text: '#fff' }, // dark teal
  { bg: '#6A1B9A', text: '#fff' }, // purple
  { bg: '#0277BD', text: '#fff' }, // light blue
  { bg: '#C62828', text: '#fff' }, // reserved/fallback
  { bg: '#FF8F00', text: '#0d1f0d' }, // amber
  { bg: '#37474F', text: '#fff' }, // blue-grey
  { bg: '#1B5E20', text: '#fff' }, // dark green
  { bg: '#880E4F', text: '#fff' }, // maroon
  { bg: '#01579B', text: '#fff' }, // deep blue
]

export function coachColorAt(index) {
  return COACH_COLORS[index % COACH_COLORS.length]
}

function minutesFromMidnight(isoStr) {
  const dt = DateTime.fromISO(isoStr).toLocal()
  return dt.hour * 60 + dt.minute
}

function isToday(day) {
  return day.hasSame(DateTime.local(), 'day')
}

function isCanceled(occurrence) {
  const ev = occurrence.event
  if (!ev) return false
  return ev.isCanceled === true
}

function isBlockTime(occurrence) {
  const ev = occurrence.event
  if (!ev) return false
  return (
    ev.__typename === 'CoachAvailabilityBlockTime' ||
    ev.__typename === 'GoogleCalendarBusyTime'
  )
}

function occurrenceLabel(occurrence) {
  if (occurrence.title) return occurrence.title
  const ev = occurrence.event
  if (!ev) return 'Event'
  if (ev.__typename === 'Session') {
    const cat = ev.lessonType?.category
    return cat === 'GROUP' ? 'Group Session' : 'Session'
  }
  if (ev.__typename === 'GroupEvent') return 'Group Event'
  return 'Blocked'
}

function enrollmentSummary(occurrence) {
  const ev = occurrence.event
  if (!ev) return null
  if (ev.__typename !== 'Session' && ev.__typename !== 'GroupEvent') return null
  const active = (ev.enrollments || []).filter(
    e => e && !e.isCanceled && e.student,
  )
  if (active.length === 0) return null
  const first = active[0].student
  const name = [first.firstName, first.lastName].filter(Boolean).join(' ')
  if (!name) return null
  return active.length > 1 ? `${name} +${active.length - 1} more` : name
}

function timeLabel(isoStr) {
  return DateTime.fromISO(isoStr).toFormat('h:mm a')
}

function getTimeWindow(coachOccurrencesMap) {
  let minMin = DEFAULT_START_MIN
  let maxMin = DEFAULT_END_MIN
  for (const occs of Object.values(coachOccurrencesMap)) {
    for (const o of occs || []) {
      if (!o.startDateTime || !o.endDateTime) continue
      const s = minutesFromMidnight(o.startDateTime)
      const e = minutesFromMidnight(o.endDateTime)
      if (s < minMin) minMin = Math.max(0, s - 30)
      if (e > maxMin) maxMin = Math.min(24 * 60, e + 30)
    }
  }
  minMin = Math.floor(minMin / 60) * 60
  maxMin = Math.ceil(maxMin / 60) * 60
  return { startMin: minMin, spanMin: maxMin - minMin }
}

function intervalsOverlap(a, b) {
  return a.start < b.end && b.start < a.end
}

/**
 * Assign horizontal lanes within each overlap cluster (Google Calendar style).
 */
function buildDayOverlapLayout(items) {
  const n = items.length
  const out = new Map()
  if (n === 0) return out

  const parent = items.map((_, i) => i)
  const find = i => (parent[i] === i ? i : (parent[i] = find(parent[i])))
  const union = (a, b) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent[ra] = rb
  }

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (intervalsOverlap(items[i], items[j])) {
        union(i, j)
      }
    }
  }

  const clusters = new Map()
  for (let i = 0; i < n; i++) {
    const r = find(i)
    if (!clusters.has(r)) clusters.set(r, [])
    clusters.get(r).push(items[i])
  }

  for (const cluster of clusters.values()) {
    if (cluster.length === 0) continue
    const sorted = cluster
      .slice()
      .sort((a, b) => a.start - b.start || b.end - a.end)
    const laneEnds = []
    const byId = new Map()
    for (const item of sorted) {
      let col = -1
      for (let i = 0; i < laneEnds.length; i++) {
        if (item.start >= laneEnds[i]) {
          col = i
          laneEnds[i] = item.end
          break
        }
      }
      if (col === -1) {
        col = laneEnds.length
        laneEnds.push(item.end)
      }
      byId.set(item.id, { col })
    }
    const k = Math.max(1, laneEnds.length)
    for (const item of cluster) {
      const { col } = byId.get(item.id)
      out.set(item.id, { col, columnCount: k })
    }
  }
  return out
}

function overlapItemHorizontalStyle(col, columnCount) {
  if (columnCount <= 1) {
    return { left: 2, right: 2, width: undefined }
  }
  const k = columnCount
  const gap = 1
  return {
    left: `calc(2px + ${col} * ((100% - 4px - ${(k - 1) *
      gap}px) / ${k} + ${gap}px))`,
    right: 'auto',
    width: `calc((100% - 4px - ${(k - 1) * gap}px) / ${k})`,
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    background: theme.palette.common.white,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  headerRow: {
    display: 'flex',
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50],
  },
  timeColHeader: {
    width: TIME_COL_WIDTH,
    minWidth: TIME_COL_WIDTH,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    background: theme.palette.grey[50],
  },
  dayHeader: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 0.5, 0.75),
    '&:last-child': { borderRight: 'none' },
  },
  dayLabel: {
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    letterSpacing: 0.3,
  },
  dayLabelToday: {
    color: theme.palette.primary.main,
  },
  dayDateChip: {
    width: 28,
    height: 28,
    fontSize: 13,
    fontWeight: 600,
    margin: '2px auto 0',
    background: 'transparent',
    color: theme.palette.text.primary,
  },
  dayDateChipToday: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  },
  gridBody: {
    display: 'flex',
    width: '100%',
  },
  timeCol: {
    width: TIME_COL_WIDTH,
    minWidth: TIME_COL_WIDTH,
    flexShrink: 0,
    background: theme.palette.common.white,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  hourLabel: {
    height: HOUR_PX,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(0.75),
    paddingTop: 2,
    fontSize: 10,
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    boxSizing: 'border-box',
  },
  dayColumns: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  dayColumn: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderRight: 'none' },
  },
  dayColumnInner: {
    position: 'relative',
    width: '100%',
  },
  hourLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    pointerEvents: 'none',
  },
  halfHourLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTop: `1px dashed ${theme.palette.divider}`,
    pointerEvents: 'none',
  },
  nowLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 2,
    background: theme.palette.error.main,
    zIndex: 2,
    pointerEvents: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -4,
      top: -4,
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: theme.palette.error.main,
    },
  },
  eventBlock: {
    position: 'absolute',
    zIndex: 1,
    borderRadius: 3,
    padding: '2px 4px',
    overflow: 'hidden',
    cursor: 'pointer',
    fontSize: 10,
    lineHeight: 1.25,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    textAlign: 'left',
    userSelect: 'none',
    boxSizing: 'border-box',
    WebkitFontSmoothing: 'antialiased',
    '&:hover': {
      filter: 'brightness(0.92)',
    },
  },
  eventBlockCompact: {
    justifyContent: 'center',
    fontSize: 9,
    lineHeight: 1.15,
    padding: '1px 3px',
  },
  eventBlockTwoLine: {
    fontSize: 9,
    lineHeight: 1.15,
    padding: '1px 3px',
    justifyContent: 'center',
  },
  eventBlockLine: {
    fontWeight: 600,
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  eventBlockMeta: {
    opacity: 0.88,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'block',
    fontSize: 9,
  },
  eventBlockCanceled: {
    opacity: 0.5,
    textDecoration: 'line-through',
  },
  blockTimeBlock: {
    opacity: 0.65,
  },
  todayCol: {
    background: 'rgba(0, 117, 202, 0.03)',
  },
}))

function occurrencesForDay(occurrences, day) {
  const dayStart = day.startOf('day')
  const dayEnd = day.endOf('day')
  return occurrences.filter(o => {
    if (!o.startDateTime || !o.endDateTime) return false
    const start = DateTime.fromISO(o.startDateTime).toLocal()
    const end = DateTime.fromISO(o.endDateTime).toLocal()
    return start <= dayEnd && end >= dayStart
  })
}

function occurrenceDaySegmentMinutes(occurrence, day) {
  const dayStart = day.startOf('day')
  const dayEnd = day.endOf('day')
  const start = DateTime.fromISO(occurrence.startDateTime).toLocal()
  const end = DateTime.fromISO(occurrence.endDateTime).toLocal()
  const segStart = start < dayStart ? dayStart : start
  const segEnd = end > dayEnd ? dayEnd : end
  return {
    startMin: segStart.hour * 60 + segStart.minute,
    endMin: segEnd.hour * 60 + segEnd.minute,
  }
}

const EventBlock = React.memo(function EventBlock({
  occurrence,
  coachColor,
  coachName,
  topPx,
  heightPx,
  horizontalStyle,
  classes,
  onClick,
}) {
  const canceled = isCanceled(occurrence)
  const blocked = isBlockTime(occurrence)
  const label = occurrenceLabel(occurrence)
  const studentLine = enrollmentSummary(occurrence)
  const startLabel = timeLabel(occurrence.startDateTime)

  const blockStyle = {
    top: topPx,
    height: heightPx,
    background: canceled
      ? '#c62828'
      : blocked
        ? coachColor.bg + 'aa'
        : coachColor.bg,
    color: coachColor.text,
    ...horizontalStyle,
  }

  const titleParts = [label]
  if (coachName) titleParts.push(coachName)
  titleParts.push(`${startLabel} – ${timeLabel(occurrence.endDateTime)}`)
  if (studentLine) titleParts.push(`with ${studentLine}`)

  const showFull = heightPx >= EVENT_BLOCK_FULL_LAYOUT_MIN_PX
  const showTwoLine = !showFull && heightPx >= EVENT_BLOCK_TWO_LINE_MIN_PX
  const showCompactTitle = !showFull && !showTwoLine
  const secondaryLine = coachName || studentLine

  return (
    <Box
      className={clsx(
        classes.eventBlock,
        canceled && classes.eventBlockCanceled,
        blocked && classes.blockTimeBlock,
        showCompactTitle && classes.eventBlockCompact,
        showTwoLine && classes.eventBlockTwoLine,
      )}
      style={blockStyle}
      title={titleParts.join(' · ')}
      role="button"
      tabIndex={0}
      aria-label={`${label}${
        coachName ? `, ${coachName}` : ''
      } at ${startLabel}`}
      onClick={() => onClick && onClick(occurrence)}
      onKeyDown={e =>
        (e.key === 'Enter' || e.key === ' ') && onClick && onClick(occurrence)
      }
    >
      {showFull && (
        <>
          <span className={classes.eventBlockLine}>{label}</span>
          {coachName && (
            <span className={classes.eventBlockMeta}>{coachName}</span>
          )}
          {!coachName && studentLine && (
            <span className={classes.eventBlockMeta}>{studentLine}</span>
          )}
          {coachName && studentLine && (
            <span className={classes.eventBlockMeta}>{studentLine}</span>
          )}
          <span className={classes.eventBlockMeta}>{startLabel}</span>
        </>
      )}
      {showTwoLine && (
        <>
          <span className={classes.eventBlockLine}>{label}</span>
          {secondaryLine && (
            <span className={classes.eventBlockMeta}>{secondaryLine}</span>
          )}
        </>
      )}
      {showCompactTitle && (
        <span className={classes.eventBlockLine}>{label}</span>
      )}
    </Box>
  )
})

EventBlock.propTypes = {
  occurrence: PropTypes.object.isRequired,
  coachColor: PropTypes.shape({ bg: PropTypes.string, text: PropTypes.string })
    .isRequired,
  coachName: PropTypes.string,
  topPx: PropTypes.number.isRequired,
  heightPx: PropTypes.number.isRequired,
  horizontalStyle: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onClick: PropTypes.func,
}

const DayColumn = React.memo(function DayColumn({
  day,
  coachEvents,
  gridStartMin,
  gridSpanMin,
  classes,
  onEventClick,
}) {
  const hourSlotCount = Math.max(1, Math.round(gridSpanMin / 60))
  const gridHeightPx = (gridSpanMin / 60) * HOUR_PX
  const todayClass = isToday(day) ? classes.todayCol : ''

  const layoutItems = useMemo(() => {
    const items = []
    for (const { occurrence, coachId, idx } of coachEvents) {
      const { startMin, endMin } = occurrenceDaySegmentMinutes(occurrence, day)
      const r0 = Math.max(0, startMin - gridStartMin)
      const r1 = Math.min(gridSpanMin, endMin - gridStartMin)
      if (r1 <= r0) continue
      items.push({
        id: `${coachId}-${occurrence.startDateTime}-${idx}`,
        start: r0,
        end: r1,
      })
    }
    return items
  }, [coachEvents, day, gridStartMin, gridSpanMin])

  const overlapLayout = useMemo(() => buildDayOverlapLayout(layoutItems), [
    layoutItems,
  ])

  const renderedEvents = useMemo(() => {
    return coachEvents
      .map(({ occurrence, coachId, coach, coachColor, idx }) => {
        const layoutId = `${coachId}-${occurrence.startDateTime}-${idx}`
        const { startMin, endMin } = occurrenceDaySegmentMinutes(
          occurrence,
          day,
        )
        const r0 = Math.max(0, startMin - gridStartMin)
        const r1 = Math.min(gridSpanMin, endMin - gridStartMin)
        if (r1 <= r0) return null

        const topPx = (r0 / 60) * HOUR_PX
        const heightPx = Math.max(12, ((r1 - r0) / 60) * HOUR_PX - 1)
        const hLay = overlapLayout.get(layoutId) || {
          col: 0,
          columnCount: 1,
        }

        return {
          layoutId,
          occurrence,
          coachId,
          coach,
          coachColor,
          topPx,
          heightPx,
          horizontalStyle: overlapItemHorizontalStyle(
            hLay.col,
            hLay.columnCount,
          ),
        }
      })
      .filter(Boolean)
  }, [coachEvents, day, gridStartMin, gridSpanMin, overlapLayout])

  const now = DateTime.local()
  const nowMin = now.hour * 60 + now.minute
  const showNow =
    isToday(day) &&
    nowMin >= gridStartMin &&
    nowMin <= gridStartMin + gridSpanMin
  const nowTopPx = showNow ? ((nowMin - gridStartMin) / 60) * HOUR_PX : null

  return (
    <Box className={`${classes.dayColumn} ${todayClass}`}>
      <Box className={classes.dayColumnInner} style={{ height: gridHeightPx }}>
        {Array.from({ length: hourSlotCount }, (_, i) => (
          <React.Fragment key={i}>
            <Box className={classes.hourLine} style={{ top: i * HOUR_PX }} />
            {i < hourSlotCount - 1 && (
              <Box
                className={classes.halfHourLine}
                style={{ top: i * HOUR_PX + HOUR_PX / 2 }}
              />
            )}
          </React.Fragment>
        ))}

        {showNow && (
          <Box className={classes.nowLine} style={{ top: nowTopPx }} />
        )}

        {renderedEvents.map(
          ({
            layoutId,
            occurrence,
            coachId,
            coach,
            coachColor,
            topPx,
            heightPx,
            horizontalStyle,
          }) => (
            <EventBlock
              key={layoutId}
              occurrence={occurrence}
              coachColor={coachColor}
              coachName={coach.name}
              topPx={topPx}
              heightPx={heightPx}
              horizontalStyle={horizontalStyle}
              classes={classes}
              onClick={occ => onEventClick && onEventClick(occ, coachId)}
            />
          ),
        )}
      </Box>
    </Box>
  )
})

DayColumn.propTypes = {
  day: PropTypes.object.isRequired,
  coachEvents: PropTypes.arrayOf(
    PropTypes.shape({
      occurrence: PropTypes.object.isRequired,
      coachId: PropTypes.string.isRequired,
      coach: PropTypes.object.isRequired,
      coachColor: PropTypes.object.isRequired,
      idx: PropTypes.number.isRequired,
    }),
  ).isRequired,
  gridStartMin: PropTypes.number.isRequired,
  gridSpanMin: PropTypes.number.isRequired,
  classes: PropTypes.object.isRequired,
  onEventClick: PropTypes.func,
}

/**
 * Academy schedule calendar — one column per day with overlapping events from
 * all selected coaches (Google Calendar style).
 */
const AcademyResourceCalendar = React.memo(function AcademyResourceCalendar({
  coaches,
  coachColorMap,
  coachOccurrencesMap,
  anchorDate,
  isSingleDay,
  suppressEvents = false,
  onEventClick,
}) {
  const classes = useStyles()
  const scrollRef = useRef(null)

  const days = useMemo(() => {
    if (isSingleDay) return [anchorDate.startOf('day')]
    const w0 = startOfUsWeek(anchorDate)
    return Array.from({ length: 7 }, (_, i) => w0.plus({ days: i }))
  }, [anchorDate, isSingleDay])

  const { startMin, spanMin } = useMemo(() => {
    if (suppressEvents) {
      return {
        startMin: DEFAULT_START_MIN,
        spanMin: DEFAULT_END_MIN - DEFAULT_START_MIN,
      }
    }
    return getTimeWindow(coachOccurrencesMap)
  }, [coachOccurrencesMap, suppressEvents])

  const eventsByDay = useMemo(() => {
    const map = {}
    for (const day of days) {
      map[day.toISODate()] = []
    }
    if (suppressEvents) return map

    for (const day of days) {
      const dayKey = day.toISODate()
      const events = []
      for (const coach of coaches) {
        const occs = coachOccurrencesMap[coach.id] || []
        const dayOccs = occurrencesForDay(occs, day)
        const color = coachColorMap[coach.id] || {
          bg: '#0075CA',
          text: '#fff',
        }
        dayOccs.forEach((occurrence, idx) => {
          events.push({
            occurrence,
            coachId: coach.id,
            coach,
            coachColor: color,
            idx,
          })
        })
      }
      map[dayKey] = events
    }
    return map
  }, [days, coaches, coachColorMap, coachOccurrencesMap, suppressEvents])

  const hourSlotCount = Math.max(1, Math.round(spanMin / 60))
  const gridHeightPx = (spanMin / 60) * HOUR_PX

  const hourLabelHours = useMemo(
    () => Array.from({ length: hourSlotCount }, (_, i) => startMin / 60 + i),
    [hourSlotCount, startMin],
  )

  useEffect(() => {
    if (!scrollRef.current) return
    const scrollTo8am = ((8 * 60 - startMin) / 60) * HOUR_PX
    scrollRef.current.scrollTop = Math.max(0, scrollTo8am - 40)
  }, [anchorDate, isSingleDay, startMin])

  if (coaches.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        color="text.secondary"
      >
        <Typography variant="body2">
          Select at least one coach to view their schedule.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.headerRow}>
        <Box className={classes.timeColHeader} />
        {days.map(day => {
          const today = isToday(day)
          const dow = isSingleDay ? day.toFormat('cccc') : day.toFormat('ccc')
          return (
            <Box key={day.toISODate()} className={classes.dayHeader}>
              <Typography
                className={`${classes.dayLabel} ${
                  today ? classes.dayLabelToday : ''
                }`}
                variant="caption"
              >
                {dow}
              </Typography>
              <Avatar
                className={`${classes.dayDateChip} ${
                  today ? classes.dayDateChipToday : ''
                }`}
              >
                {day.day}
              </Avatar>
            </Box>
          )
        })}
      </Box>

      <Box ref={scrollRef} className={classes.scrollArea}>
        <Box className={classes.gridBody}>
          <Box className={classes.timeCol} style={{ height: gridHeightPx }}>
            {hourLabelHours.map(h => {
              const hourInt = Math.min(23, Math.max(0, Math.floor(h)))
              return (
                <Box key={`h-${h}`} className={classes.hourLabel}>
                  {DateTime.fromObject({
                    year: 2000,
                    month: 1,
                    day: 1,
                    hour: hourInt,
                    minute: 0,
                  }).toFormat('h a')}
                </Box>
              )
            })}
          </Box>

          <Box className={classes.dayColumns}>
            {days.map(day => (
              <DayColumn
                key={day.toISODate()}
                day={day}
                coachEvents={eventsByDay[day.toISODate()] || []}
                gridStartMin={startMin}
                gridSpanMin={spanMin}
                classes={classes}
                onEventClick={onEventClick}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
})

AcademyResourceCalendar.propTypes = {
  coaches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
    }),
  ).isRequired,
  coachColorMap: PropTypes.object.isRequired,
  coachOccurrencesMap: PropTypes.object.isRequired,
  anchorDate: PropTypes.object.isRequired,
  isSingleDay: PropTypes.bool,
  suppressEvents: PropTypes.bool,
  onEventClick: PropTypes.func,
}

AcademyResourceCalendar.defaultProps = {
  isSingleDay: false,
}

export default AcademyResourceCalendar
